import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { HostReviewsDocument } from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import HostReviewCard from './HostReviewCard';

type Props = {
  urlSlug: string;
};

export default function HostReviews({ urlSlug }: Props) {
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, error, loading, fetchMore } = useQuery(HostReviewsDocument, {
    variables: {
      urlSlug,
    },
  });

  const nextToken = data?.hostByURL?.reviews?.nextToken ?? null;

  async function fetchMoreReviews() {
    setLoadingMore(true);
    await fetchMore({
      variables: {
        nextToken,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          ...prev,
          hostByURL: {
            ...prev.hostByURL,
            reviews: {
              ...prev.hostByURL.reviews,
              items: [
                ...prev.hostByURL.reviews.items,
                ...fetchMoreResult.hostByURL.reviews.items,
              ],
              nextToken: fetchMoreResult.hostByURL.reviews.nextToken,
            },
          },
        };
      },
    });
    setLoadingMore(false);
  }

  if (loading && !data) {
    return <LoadingPage />;
  }

  if (error && !data) {
    return <ErrorPage />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-4 mt-3">
        <h1 className="hero-heading mb-0">Reviews</h1>
      </div>
      {data.hostByURL.reviews.items.length > 0 ? (
        data.hostByURL.reviews.items.map((review) => (
          <HostReviewCard
            review={review}
            key={review.id}
            host={{
              name: data.hostByURL.name,
              id: data.hostByURL.id,
            }}
          />
        ))
      ) : (
        <Alert variant="info">
          <p>
            <strong>You don't have any reviews yet.</strong>
          </p>
        </Alert>
      )}
      {nextToken ? (
        <div className={'d-grid gap-2'}>
          <Button
            variant="primary"
            type="button"
            disabled={loadingMore}
            onClick={fetchMoreReviews}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </>
  );
}
